import { Component, OnInit } from '@angular/core';
import {
  DashboardMetadata,
  DeleteChartOrDashboardInput,
  EntityType,
  DeleteChartOrDashboardMutation,
  DeleteChartOrDashboardMutationVariables,
  DeleteResponse,
  GetDashboardsQuery,
  GetDashboardsQueryVariables,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DeleteChartOrDashboard } from '../../../../graphql/mutations';
import { GetDashboards } from '../../../../graphql/queries';
import { ViewDashboardsComponent } from '../view-dashboards/view-dashboards.component';
import { CreateDashboardComponent } from '../create-dashboard/create-dashboard.component';
import { compareDate, compareText } from '../../../shared/utils/sort.utils';

@Component({
  selector: 'app-dashboards',
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
})
export class DashboardsComponent implements OnInit {
  dashHeaders: any[] = [];

  dashboards: DashboardMetadata[] = [];
  isViewModalVisible = false;
  isViewModalLoading = false;
  isLoading = false;
  filteredDashboards: DashboardMetadata[] = [];
  total = 0;

  trackByHeader(index: number, item: { name: string }): string {
    return item.name;
  }

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
  ) {
    this.setHeaders();
  }
  ngOnInit(): void {
    this.loadDashboards();
  }

  setHeaders(): void {
    this.dashHeaders = [
      {
        name: 'Dashboard Name',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (first: DashboardMetadata, second: DashboardMetadata) => compareText(first.name, second.name),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Number of Charts used',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (first: DashboardMetadata, second: DashboardMetadata) =>
          (first.chartIds?.length ?? 0) - (second.chartIds?.length ?? 0),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Last Modified On',
        width: '8.75rem',
        sortOrder: 'descend',
        sortFn: (first: DashboardMetadata, second: DashboardMetadata) =>
          compareDate(first.lastModifiedAt, second.lastModifiedAt),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Last Modified By',
        width: '12.5rem',
        sortOrder: null,
        sortFn: (first: DashboardMetadata, second: DashboardMetadata) =>
          compareText(first.lastModifiedBy, second.lastModifiedBy),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Action',
        width: '7rem',
        right: '0px',
        sortOrder: null,
        sortFn: null,
        sortDirections: [],
      },
    ];
  }

  private loadDashboards(): void {
    const orgId = this.userManagementService.getOrgId();
    if (!orgId) return;

    this.isLoading = true;
    this.apiService
      .query<GetDashboardsQuery, GetDashboardsQueryVariables>(GetDashboards, {
        orgId,
      })
      .then((response) => {
        this.dashboards = (response.data?.GetDashboards as DashboardMetadata[]) || [];
        this.applyFiltersAndSort();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts: ${error}`);
        this.dashboards = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private applyFiltersAndSort(): void {
    this.filteredDashboards = [...this.dashboards];
    this.total = this.filteredDashboards.length;
  }

  deleteDashboard(dashboardData: DashboardMetadata): void {
    this.modalService.confirm({
      nzTitle: `Are you sure you want to delete this dashboard?`,
      nzContent:
        'Deleting this dashboard will permanently remove it from your workspace. This action cannot be undone.',
      nzOkText: 'Yes, Delete',
      nzOnOk: () => this.delete(dashboardData),
      nzCancelText: 'Cancel',
    });
  }
  private delete(dashboard: DashboardMetadata) {
    this.isLoading = true;
    const deleteInput: DeleteChartOrDashboardInput = {
      orgId: dashboard.orgId,
      entityId: dashboard.dashboardId,
      entityType: EntityType.DASHBOARD,
    };
    this.apiService
      .query<DeleteChartOrDashboardMutation, DeleteChartOrDashboardMutationVariables>(DeleteChartOrDashboard, {
        input: deleteInput,
      })
      .then((response) => {
        const deleteResponse = response.data?.DeleteChartOrDashboard as DeleteResponse;
        if (deleteResponse.statusCode !== 200) {
          this.nzMessageService.error(`Failed to delete dashboard: ${deleteResponse.message}`);
        } else {
          this.nzMessageService.success(`Dashboard deleted successfully`);
          this.loadDashboards();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to delete dashboard: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  viewDashboard(dashboardData: DashboardMetadata): void {
    const dashboardName = dashboardData?.name ?? '';
    this.modalService.create({
      nzTitle: dashboardName,
      nzFooter: null,
      nzContent: ViewDashboardsComponent,
      nzData: { dashboardData: dashboardData },
      nzMaskClosable: false,
      nzWidth: '90vw',
    });
  }

  openCreateDashboardModal(): void {
    this.modalService.create({
      nzTitle: 'Create Dashboard',
      nzFooter: null,
      nzContent: CreateDashboardComponent,
      nzMaskClosable: false,
      nzWidth: '90vw',
      nzOnOk: () => {
        this.loadDashboards();
      },
      nzOnCancel: () => {
        this.loadDashboards();
      },
    });
  }

  openEditDashboardModal(dashboardData: DashboardMetadata): void {
    this.modalService.create({
      nzTitle: 'Edit Dashboard',
      nzFooter: null,
      nzContent: CreateDashboardComponent,
      nzMaskClosable: false,
      nzData: { dashboardId: dashboardData.dashboardId },
      nzWidth: '90vw',
      nzOnOk: () => {
        this.loadDashboards();
      },
      nzOnCancel: () => {
        this.loadDashboards();
      },
    });
  }
}
